import React, { useState, useRef, useEffect } from 'react';
import { FiBell, FiCheckCircle, FiChevronDown, FiClock, FiCopy, FiEdit2, FiLink, FiPlus, FiSearch, FiSend } from 'react-icons/fi';
import { Show, Artist } from '../../../../model/FestivalData';
import {
  sendNotification,
  CustomNotificationTopic,
  getTopics,
  upsertTopic,
  getSubdomainFromFestivalId,
  ScheduledNotification,
  fetchScheduledNotifications,
  createScheduledNotification,
  updateScheduledNotification,
  cancelScheduledNotification,
} from '../../../../services/api';
import { generateCanonicalName, generateIdentifier } from '../../../../utils/identifiers';
import PageHeader from '../../../../components/PageHeader';
import { useDialogs } from '../../../../components/Dialogs';
import { useLanguage, useMessages } from '../../../../i18n/I18n';
import './NotificationsEditor.css';
import './ShareLinkModal.css';
import { loadTabData } from '../../../../services/tabData';

interface NotificationsEditorProps {
  shows: Show[];
  artists: Artist[];
  sessionToken: string;
  festivalId: string;
}

interface NotificationForm {
  topic_identifier: string;
  title: string;
  body: string;
}

interface DropdownOption {
  value: string;
  label: string;
  group?: string;
}

interface DropdownSection {
  label: string;
  options: DropdownOption[];
}

const all_users_push_topic = 'all_users';

const NotificationsEditor: React.FC<NotificationsEditorProps> = ({
  shows,
  artists,
  sessionToken,
  festivalId,
}) => {
  const t = useMessages('notifications');
  const common = useMessages('common');
  const { locale } = useLanguage();
  const [form, setForm] = useState<NotificationForm>({
    topic_identifier: '',
    title: '',
    body: '',
  });
  const [showModal, setShowModal] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState(t.sent);
  const [recipientFilter, setRecipientFilter] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [customTopics, setCustomTopics] = useState<CustomNotificationTopic[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingTopic, setEditingTopic] = useState<CustomNotificationTopic | null>(null);
  const [newTopicName, setNewTopicName] = useState('');
  const [copySuccess, setCopySuccess] = useState<string | null>(null);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [currentLink, setCurrentLink] = useState('');

  // Scheduling
  const [mode, setMode] = useState<'now' | 'schedule'>('now');
  const [scheduledAt, setScheduledAt] = useState('');
  const [scheduledNotifications, setScheduledNotifications] = useState<ScheduledNotification[]>([]);
  const [editingScheduledId, setEditingScheduledId] = useState<string | null>(null);
  const { confirm, alert } = useDialogs();

  const loadScheduledNotifications = async () => {
    try {
      const items = await loadTabData(
        festivalId, 'scheduledNotifications', () => fetchScheduledNotifications(sessionToken, festivalId),
      );
      setScheduledNotifications(items);
    } catch (error) {
      console.error('Failed to fetch scheduled notifications:', error);
    }
  };

  useEffect(() => {
    loadScheduledNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionToken, festivalId]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchTopics = async () => {
      try {
        const topics = await loadTabData(festivalId, 'topics', () => getTopics(sessionToken, festivalId));
        setCustomTopics(topics);
      } catch (error) {
        console.error('Failed to fetch topics:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchTopics();
  }, [sessionToken, festivalId]);

  const getArtistName = (artistId: string): string => {
    return artists.find(artist => artist.identifier === artistId)?.name || t.unknownArtist;
  };

  const resolveTopic = (): string =>
    form.topic_identifier === all_users_push_topic || customTopics.some(topic => topic.identifier === form.topic_identifier)
      ? form.topic_identifier
      : `show_${form.topic_identifier}`;

  // Validates the scheduled time. Returns an error message, or null if valid.
  const validateScheduledAt = (): string | null => {
    if (!scheduledAt) return t.pickDateTime;
    const when = new Date(scheduledAt).getTime();
    const now = Date.now();
    if (Number.isNaN(when)) return t.invalidDate;
    if (when <= now) return t.mustBeFuture;
    if (when > now + 30 * 24 * 60 * 60 * 1000) return t.mustBeWithin30Days;
    return null;
  };

  const resetForm = () => {
    setForm({ topic_identifier: '', title: '', body: '' });
    setScheduledAt('');
    setMode('now');
    setEditingScheduledId(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (mode === 'schedule') {
      handleSchedule();
    } else {
      setShowModal(true);
    }
  };

  const handleSend = async () => {
    try {
      await sendNotification(sessionToken, festivalId, {
        topic: resolveTopic(),
        title: form.title,
        body: form.body,
      });

      setShowModal(false);
      setSuccessMessage(t.sent);
      setIsSuccess(true);
      resetForm();

      // Hide success message after 3 seconds
      setTimeout(() => {
        setIsSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Failed to send notification:', error);
      alert(t.sendFailed, { title: t.sendFailedTitle });
    }
  };

  const handleSchedule = async () => {
    const validationError = validateScheduledAt();
    if (validationError) {
      alert(validationError, { title: t.scheduleFailedTitle });
      return;
    }

    const payload = {
      topic: resolveTopic(),
      title: form.title,
      body: form.body,
      scheduled_at: new Date(scheduledAt).toISOString(),
    };

    try {
      if (editingScheduledId) {
        await updateScheduledNotification(sessionToken, festivalId, editingScheduledId, payload);
        setSuccessMessage(t.scheduledUpdated);
      } else {
        await createScheduledNotification(sessionToken, festivalId, payload);
        setSuccessMessage(t.scheduled);
      }
      setIsSuccess(true);
      resetForm();
      await loadScheduledNotifications();
      setTimeout(() => setIsSuccess(false), 3000);
    } catch (error) {
      console.error('Failed to schedule notification:', error);
      alert(error instanceof Error ? error.message : t.scheduleFailed, {
        title: t.scheduleFailedTitle,
      });
    }
  };

  const handleEditScheduled = (notification: ScheduledNotification) => {
    // Map the persisted topic back to the dropdown's identifier (show_<id> -> <id>).
    const topicIdentifier = notification.topic.startsWith('show_')
      ? notification.topic.slice('show_'.length)
      : notification.topic;
    setForm({ topic_identifier: topicIdentifier, title: notification.title, body: notification.body });
    // datetime-local expects local "YYYY-MM-DDTHH:mm"; build it from the UTC ISO string.
    const localDate = new Date(notification.scheduled_at);
    const offsetMs = localDate.getTimezoneOffset() * 60 * 1000;
    setScheduledAt(new Date(localDate.getTime() - offsetMs).toISOString().slice(0, 16));
    setMode('schedule');
    setEditingScheduledId(notification.identifier);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelScheduled = async (identifier: string) => {
    if (!await confirm(t.cancelConfirm, {
      title: t.cancelConfirmTitle,
      confirmLabel: t.cancelConfirmLabel,
      cancelLabel: t.keepIt,
      danger: true,
    })) return;
    try {
      await cancelScheduledNotification(sessionToken, festivalId, identifier);
      await loadScheduledNotifications();
    } catch (error) {
      console.error('Failed to cancel scheduled notification:', error);
      alert(error instanceof Error ? error.message : t.cancelFailed, {
        title: t.cancelFailedTitle,
      });
    }
  };

  const recipientLabelForTopic = (topic: string, topicName: string): string => {
    if (topic === all_users_push_topic) return topicName || t.allUsers;
    if (topic.startsWith('show_')) {
      const showId = topic.slice('show_'.length);
      const show = shows.find(s => s.identifier === showId);
      return show ? t.subscribersOf(getArtistName(show.artist_id)) : topicName || topic;
    }
    return topicName || topic;
  };

  const handleCreateTopic = async () => {
    const canonicalName = generateCanonicalName(recipientFilter);
    const newTopic: CustomNotificationTopic = {
      identifier: generateIdentifier(canonicalName),
      name: recipientFilter,
    };

    try {
      await upsertTopic(sessionToken, festivalId, newTopic);
      setCustomTopics([...customTopics, newTopic]);
      setForm({ ...form, topic_identifier: newTopic.identifier });
      setIsDropdownOpen(false);
      setRecipientFilter('');
    } catch (error) {
      console.error('Failed to create topic:', error);
      alert(t.createTopicFailed, { title: t.createTopicFailedTitle });
    }
  };

  const handleUpdateTopic = async () => {
    if (!editingTopic || !newTopicName.trim()) return;
    
    try {
      const updatedTopic = {
        ...editingTopic,
        name: newTopicName.trim()
      };
      
      await upsertTopic(sessionToken, festivalId, updatedTopic);
      
      setCustomTopics(prevTopics => 
        prevTopics.map(topic => 
          topic.identifier === editingTopic.identifier ? updatedTopic : topic
        )
      );
      
      setEditingTopic(null);
      setNewTopicName('');
    } catch (error) {
      console.error('Failed to update topic:', error);
      alert(t.renameTopicFailed, { title: t.renameTopicFailedTitle });
    }
  };

  const handleCopyLink = async (topicIdentifier: string) => {
    try {
      // Get hostname and check if we're on localhost
      const hostname = window.location.hostname;
      let subdomain = hostname.split('.')[0];
      
      if (hostname === 'localhost' || hostname === '127.0.0.1' || subdomain === hostname) {
        // If on localhost, fetch subdomain from API
        subdomain = await getSubdomainFromFestivalId(festivalId);
      }

      const subscribeLink = `${process.env.REACT_APP_API_BASE_URL}/${subdomain}/deeplink/notification/subscribe/${topicIdentifier}`;
      const topic = customTopics.find(custom => custom.identifier === topicIdentifier);
      setCurrentLink(subscribeLink);
      setNewTopicName(topic?.name || '');
      setShowLinkModal(true);
    } catch (error) {
      console.error('Failed to get link:', error);
      setCopySuccess(t.linkFailed);
      setTimeout(() => setCopySuccess(null), 3000);
    }
  };

  const attemptCopy = async () => {
    try {
      await navigator.clipboard.writeText(currentLink);
      setCopySuccess(t.linkCopied);
    } catch (error) {
      console.error('Copy failed:', error);
      setCopySuccess(t.copyManually);
    }
    setTimeout(() => setCopySuccess(null), 3000);
  };

  const sections: DropdownSection[] = [
    {
      label: t.general,
      options: [
        ...(customTopics.some(topic => topic.identifier === all_users_push_topic) 
          ? []
          : [{
              value: all_users_push_topic,
              label: t.allUsers,
            }]
        ),
        ...customTopics
          .map(topic => ({
            value: topic.identifier,
            label: topic.identifier === all_users_push_topic 
              ? topic.name 
              : `${topic.name} (code=${topic.identifier})`
          }))
      ]
    },
    {
      label: t.showSubscribers,
      options: shows.map(show => ({
        value: show.identifier,
        label: getArtistName(show.artist_id)
      }))
    }
  ];

  const filteredSections = sections.map(section => ({
    ...section,
    options: section.options.filter(option =>
      option.label.toLowerCase().includes(recipientFilter.toLowerCase())
    )
  })).filter(section => section.options.length > 0);

  const selectedOption = sections
    .flatMap(section => section.options)
    .find(opt => opt.value === form.topic_identifier) || { value: '', label: t.selectRecipients };

  // Add keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsDropdownOpen(false);
    }
  };

  // Prevent dropdown close when clicking filter
  const handleFilterClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  // Focus filter input when dropdown opens
  useEffect(() => {
    if (isDropdownOpen) {
      const filterInput = document.querySelector('.recipient-filter') as HTMLInputElement;
      if (filterInput) {
        filterInput.focus();
      }
    }
  }, [isDropdownOpen]);

  return (
    <div className="notifications-editor">
      <PageHeader
        title={t.title}
        description={t.description}
      />

      {isSuccess && (
        <div className="success-alert" role="status">
          <FiCheckCircle aria-hidden="true" />
          {successMessage}
        </div>
      )}

      <div className="notification-compose">
        <form className="notification-composer" onSubmit={handleSubmit}>
          <div className="composer-header">
            <h3>{editingScheduledId ? t.editScheduled : t.newNotification}</h3>
          </div>

          <div className="composer-body">
            <div className="form-group">
              <label id="recipients-label">{t.recipients}</label>
              <div className={`custom-dropdown${isDropdownOpen ? ' open' : ''}`} ref={dropdownRef}>
                <div
                  className="dropdown-header"
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  role="combobox"
                  aria-expanded={isDropdownOpen}
                  aria-haspopup="listbox"
                  aria-labelledby="recipients-label"
                  tabIndex={0}
                  onKeyDown={handleKeyDown}
                >
                  <span className={selectedOption.value ? undefined : 'placeholder'}>{selectedOption.label}</span>
                  <FiChevronDown className="dropdown-arrow" aria-hidden="true" />
                </div>

                {isDropdownOpen && (
                  <div className="dropdown-content">
                    <div className="filter-container">
                      <FiSearch aria-hidden="true" />
                      <input
                        type="text"
                        placeholder={t.searchRecipients}
                        value={recipientFilter}
                        onChange={(e) => setRecipientFilter(e.target.value)}
                        className="recipient-filter"
                        onClick={handleFilterClick}
                        onKeyDown={handleKeyDown}
                      />
                    </div>
                    <div
                      className="dropdown-options"
                      role="listbox"
                    >
                      {filteredSections.map((section) => (
                        <div key={section.label} className="dropdown-section">
                          <div className="dropdown-section-header">
                            {section.label}
                            <span className="section-count">
                              {section.options.length}
                            </span>
                          </div>
                          {section.options.map((option) => (
                            <div
                              key={option.value}
                              className={`dropdown-option ${form.topic_identifier === option.value ? 'selected' : ''}`}
                            >
                              <div
                                className="dropdown-option-content"
                                onClick={() => {
                                  setForm({ ...form, topic_identifier: option.value });
                                  setIsDropdownOpen(false);
                                  setRecipientFilter('');
                                }}
                                role="option"
                                aria-selected={form.topic_identifier === option.value}
                              >
                                {option.label}
                              </div>
                              {(customTopics.some(t => t.identifier === option.value) || 
                                option.value === all_users_push_topic) && (
                                <div className="topic-actions">
                                  {option.value !== all_users_push_topic && (
                                    <button
                                      type="button"
                                      className="topic-action-button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleCopyLink(option.value);
                                      }}
                                      title={t.shareLink}
                                      aria-label={t.shareLink}
                                    >
                                      <FiLink aria-hidden="true" />
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    className="topic-action-button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const topic = customTopics.find(custom => custom.identifier === option.value) || 
                                        (option.value === all_users_push_topic 
                                          ? { identifier: all_users_push_topic, name: t.allUsers }
                                          : null);
                                      if (topic) {
                                        setEditingTopic(topic);
                                        setNewTopicName(topic.name);
                                      }
                                    }}
                                    title={t.renameTopic}
                                    aria-label={t.renameTopic}
                                  >
                                    <FiEdit2 aria-hidden="true" />
                                  </button>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ))}
                      {filteredSections.length === 0 && (
                        <div className="dropdown-no-results">
                          <div>{t.noMatch(recipientFilter)}</div>
                          <button
                            type="button"
                            className="btn create-topic-button"
                            onClick={handleCreateTopic}
                          >
                            <FiPlus aria-hidden="true" />
                            {t.createTopic(recipientFilter)}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="form-group">
              <div className="field-label-row">
                <label htmlFor="title">{t.titleLabel}</label>
                <span className={`char-count${form.title.length >= 50 ? ' full' : ''}`}>{form.title.length}/50</span>
              </div>
              <input
                type="text"
                id="title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                required
                maxLength={50}
              />
            </div>

            <div className="form-group">
              <div className="field-label-row">
                <label htmlFor="body">{t.message}</label>
                <span className={`char-count${form.body.length >= 200 ? ' full' : ''}`}>{form.body.length}/200</span>
              </div>
              <textarea
                id="body"
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                required
                maxLength={200}
                rows={4}
              />
            </div>

            <div className="form-group">
              <span className="field-label" id="delivery-label">{t.delivery}</span>
              <div className="segmented delivery-mode-toggle" role="group" aria-labelledby="delivery-label">
                <button
                  type="button"
                  className={`delivery-mode-option ${mode === 'now' ? 'active' : ''}`}
                  aria-pressed={mode === 'now'}
                  onClick={() => setMode('now')}
                  disabled={editingScheduledId !== null}
                >
                  {t.sendNow}
                </button>
                <button
                  type="button"
                  className={`delivery-mode-option ${mode === 'schedule' ? 'active' : ''}`}
                  aria-pressed={mode === 'schedule'}
                  onClick={() => setMode('schedule')}
                >
                  {t.schedule}
                </button>
              </div>
            </div>

            {mode === 'schedule' && (
              <div className="form-group">
                <label htmlFor="scheduled_at">{t.scheduledTime}</label>
                <input
                  type="datetime-local"
                  id="scheduled_at"
                  value={scheduledAt}
                  onChange={(e) => setScheduledAt(e.target.value)}
                  required
                />
                <small className="field-hint">{t.scheduledTimeHint}</small>
              </div>
            )}
          </div>

          <div className="composer-footer">
            {editingScheduledId && (
              <button type="button" className="btn btn-ghost cancel-edit-button" onClick={resetForm}>
                {t.cancelEdit}
              </button>
            )}
            <button
              type="submit"
              className="btn btn-primary send-button"
              disabled={!form.topic_identifier || (mode === 'schedule' && !scheduledAt)}
            >
              {mode === 'schedule' ? <FiClock aria-hidden="true" /> : <FiSend aria-hidden="true" />}
              {mode === 'schedule'
                ? (editingScheduledId ? t.updateScheduled : t.scheduleNotification)
                : t.sendNotification}
            </button>
          </div>
        </form>

        {/* The typed title and message on a phone's lock screen: presentation only, hidden from assistive tech */}
        <aside className="lockscreen-preview" aria-hidden="true">
          <span className="lockscreen-preview-label">{t.preview}</span>
          <div className="lockscreen">
            <span className="lockscreen-time">9:41</span>
            <div className="lockscreen-notification">
              <span className="lockscreen-app-icon">
                <FiBell />
              </span>
              <div className="lockscreen-text">
                <div className="lockscreen-heading">
                  <span className={`lockscreen-title${form.title ? '' : ' empty'}`}>{form.title || t.titleLabel}</span>
                  <span className="lockscreen-when">{t.previewNow}</span>
                </div>
                <span className={`lockscreen-body${form.body ? '' : ' empty'}`}>
                  {form.body || t.previewBody}
                </span>
              </div>
            </div>
          </div>
        </aside>
      </div>

      <section className="scheduled-notifications-section data-table" aria-labelledby="scheduled-notifications-title">
        <h3 id="scheduled-notifications-title" className="scheduled-notifications-title">{t.scheduledList}</h3>
        {scheduledNotifications.length === 0 ? (
          <p className="data-table-empty scheduled-empty">{t.noScheduled}</p>
        ) : (
          <div className="data-table-scroll">
            <div className="scheduled-table" role="table" aria-labelledby="scheduled-notifications-title">
              <div className="data-table-head" role="row">
                <span role="columnheader">{t.titleLabel}</span>
                <span role="columnheader">{t.recipient}</span>
                <span role="columnheader">{t.scheduledTime}</span>
                <span role="columnheader">{t.status}</span>
                <span role="columnheader" className="scheduled-actions-head">{t.actions}</span>
              </div>
              {scheduledNotifications.map((notification) => (
                <div
                  key={notification.identifier}
                  role="row"
                  className={`data-table-row${editingScheduledId === notification.identifier ? ' editing' : ''}`}
                >
                  <span role="cell" className="scheduled-title">{notification.title}</span>
                  <span role="cell">{recipientLabelForTopic(notification.topic, notification.topic_name)}</span>
                  <span role="cell" className="scheduled-time">{new Date(notification.scheduled_at).toLocaleString(locale)}</span>
                  <span role="cell">
                    <span className={`scheduled-status ${notification.status}`}>
                      {t.statusLabel(notification.status)}
                    </span>
                  </span>
                  <span role="cell" className="scheduled-actions">
                    {notification.status === 'pending' && (
                      <>
                        <button
                          type="button"
                          className="btn"
                          onClick={() => handleEditScheduled(notification)}
                        >
                          {common.edit}
                        </button>
                        <button
                          type="button"
                          className="btn btn-danger"
                          onClick={() => handleCancelScheduled(notification.identifier)}
                        >
                          {common.cancel}
                        </button>
                      </>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content notifications-dialog">
            <h3>{t.sendConfirm}</h3>
            <div className="notification-preview">
              <p><strong>{t.recipientsColon}</strong> {
                form.topic_identifier === all_users_push_topic 
                  ? (customTopics.find(topic => topic.identifier === all_users_push_topic)?.name || t.allUsers)
                  : shows.find(s => s.identifier === form.topic_identifier)
                    ? t.subscribersOf(getArtistName(shows.find(s => s.identifier === form.topic_identifier)?.artist_id || ''))
                    : customTopics.find(topic => topic.identifier === form.topic_identifier)?.name || t.unknown
              }</p>
              <p><strong>{t.titleColon}</strong> {form.title}</p>
              <p><strong>{t.messageColon}</strong> {form.body}</p>
            </div>
            <div className="modal-actions">
              <button onClick={handleSend}>
                <FiSend aria-hidden="true" />
                {t.send}
              </button>
              <button onClick={() => setShowModal(false)}>
                {common.cancel}
              </button>
            </div>
          </div>
        </div>
      )}

      {editingTopic && (
        <div className="modal-overlay">
          <div className="modal-content notifications-dialog">
            <h3>{t.renameTopic}</h3>
            <div className="form-group">
              <label htmlFor="topicName">{t.topicName}</label>
              <input
                type="text"
                id="topicName"
                value={newTopicName}
                onChange={(e) => setNewTopicName(e.target.value)}
                required
                maxLength={50}
              />
            </div>
            <div className="modal-actions">
              <button onClick={handleUpdateTopic}>
                {common.save}
              </button>
              <button 
                onClick={() => {
                  setEditingTopic(null);
                  setNewTopicName('');
                }} 
              >
                {common.cancel}
              </button>
            </div>
          </div>
        </div>
      )}

      {showLinkModal && (
        <div className="modal-overlay">
          <div className="modal-content notifications-dialog share-link-dialog">
            <h3>{t.shareLink}</h3>
            <p className="modal-description">
              {t.shareLinkDescription(newTopicName)}
            </p>
            <div className="link-container">
              <input
                type="text"
                value={currentLink}
                readOnly
                onClick={(e) => e.currentTarget.select()}
                className="share-link-input"
                aria-label={t.subscriptionLink}
              />
              <button type="button" onClick={attemptCopy} className="btn copy-button">
                <FiCopy aria-hidden="true" />
                {t.copy}
              </button>
            </div>
            <div className="modal-actions">
              <button onClick={() => setShowLinkModal(false)}>
                {common.close}
              </button>
            </div>
          </div>
        </div>
      )}

      {copySuccess && (
        <div className="success-message" role="status">
          {copySuccess}
        </div>
      )}
    </div>
  );
};

export default NotificationsEditor; 